import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Modal from '../../components/ui/Modal';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Button from '../../components/ui/Button';
import Tabs from '../../components/ui/Tabs';
import { listDocumentTypes, updateWorkflowStep } from '../../api/workflow';
import { pushToast } from '../../components/ui/toastBus';
import { OPTION_TYPES } from '../../components/fields/FieldEditor';
import FieldSectionsEditor from '../../components/fields/FieldSectionsEditor';

export default function WorkflowStepEditModal({ open, onClose, workflowStep }) {
  const queryClient = useQueryClient();
  const typesQuery = useQuery({ queryKey: ['document-types'], queryFn: listDocumentTypes, enabled: open });

  const [activeTab, setActiveTab] = useState('general');
  const [title, setTitle] = useState('');
  const [applyTitleToAll, setApplyTitleToAll] = useState(true);
  const [checklist, setChecklist] = useState([]);
  const [requiredDocs, setRequiredDocs] = useState([]);
  const [fields, setFields] = useState([]);
  const [fieldSections, setFieldSections] = useState([]);
  const [slaDays, setSlaDays] = useState('');
  const [skipEnabled, setSkipEnabled] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (workflowStep) {
      setActiveTab('general');
      setTitle(workflowStep.title || '');
      setApplyTitleToAll(!workflowStep.title_override);
      setChecklist(workflowStep.checklist || []);
      setRequiredDocs((workflowStep.required_documents || []).map((d) => ({ required: true, verify: true, depends_on: null, ...d })));
      setFields(
        (workflowStep.fields || []).map((f) => ({
          ...f,
          options: f.options || [],
          validation: { required: false, date_rule: null, ...(f.validation || {}) },
          show_in_basic_details: f.show_in_basic_details ?? false,
          depends_on: f.depends_on ?? null,
          section_id: f.section_id ?? null,
        }))
      );
      setFieldSections(workflowStep.field_sections || []);
      setSlaDays(workflowStep.sla_days ?? '');
      setSkipEnabled(!!workflowStep.skip_enabled);
      setError('');
    }
  }, [workflowStep]);

  const mutation = useMutation({
    mutationFn: () =>
      updateWorkflowStep(workflowStep.id, {
        title: title.trim(),
        apply_title_to_all_workflows: applyTitleToAll,
        checklist: checklist.map((s) => s.trim()).filter(Boolean),
        required_documents: requiredDocs,
        fields,
        field_sections: fieldSections,
        sla_days: String(slaDays).trim() === '' ? null : Number(slaDays),
        skip_enabled: skipEnabled,
      }),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Step updated.' });
      queryClient.invalidateQueries({ queryKey: ['workflow-categories'] });
      onClose();
    },
    onError: (err) => {
      if (err.response?.data?.errors?.fields) setActiveTab('fields');
      else if (err.response?.data?.errors?.required_documents) setActiveTab('fields');
      if (err.response?.data?.errors?.title) setActiveTab('general');
      setError(
        err.response?.data?.errors?.fields?.[0] ||
          err.response?.data?.errors?.required_documents?.[0] ||
          err.response?.data?.errors?.title?.[0] ||
          err.response?.data?.message ||
          'Could not update step.'
      );
    },
  });

  const docTypeName = (code) => typesQuery.data?.find((t) => t.code === code)?.name || code;
  const activeDocTypes = (typesQuery.data || []).filter((t) => t.is_active !== false);
  const availableDocTypes = activeDocTypes.filter((t) => !requiredDocs.some((d) => d.code === t.code));
  const fieldLabel = (fieldId) => fields.find((f) => f.id === fieldId)?.label || fieldId;

  const addDoc = (code) => {
    if (!code || requiredDocs.some((d) => d.code === code)) return;
    setRequiredDocs((prev) => [...prev, { code, required: true, verify: true, depends_on: null }]);
  };

  const removeDoc = (code) => setRequiredDocs((prev) => prev.filter((d) => d.code !== code));

  const setDocOption = (code, patch) => setRequiredDocs((prev) => prev.map((d) => (d.code === code ? { ...d, ...patch } : d)));

  const setChecklistItem = (idx, value) => setChecklist((prev) => prev.map((item, i) => (i === idx ? value : item)));
  const removeChecklistItem = (idx) => setChecklist((prev) => prev.filter((_, i) => i !== idx));
  const addChecklistItem = () => setChecklist((prev) => [...prev, '']);

  const handleSave = () => {
    setError('');
    if (!title.trim()) {
      setActiveTab('general');
      setError('Step name cannot be empty.');
      return;
    }
    const blankSection = fieldSections.find((s) => !s.title.trim());
    if (blankSection) {
      setActiveTab('fields');
      setError('Every section needs a title.');
      return;
    }
    const blank = fields.find((f) => !f.label.trim());
    if (blank) {
      setActiveTab('fields');
      setError('Every field needs a label.');
      return;
    }
    const badOptions = fields.find((f) => OPTION_TYPES.includes(f.type) && f.options.filter((o) => o.trim()).length < 2);
    if (badOptions) {
      setActiveTab('fields');
      setError(`"${badOptions.label}" needs at least 2 answer options.`);
      return;
    }
    mutation.mutate();
  };

  if (!workflowStep) return null;

  const TABS = [
    { value: 'general', label: 'General' },
    { value: 'checklist', label: `Checklist${checklist.length ? ` (${checklist.length})` : ''}` },
    { value: 'documents', label: `Documents${requiredDocs.length ? ` (${requiredDocs.length})` : ''}` },
    { value: 'fields', label: `Fields${fields.length ? ` (${fields.length})` : ''}` },
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Edit: ${title || workflowStep.title}`}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={mutation.isPending}>
            Save changes
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

        {activeTab === 'general' && (
          <div>
            <Input
              label="Step name"
              placeholder="Step name"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <div className="mt-2 space-y-1.5">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="rename-scope"
                  className="accent-primary"
                  checked={applyTitleToAll}
                  onChange={() => setApplyTitleToAll(true)}
                />
                Rename in every workflow that uses this step
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="rename-scope"
                  className="accent-primary"
                  checked={!applyTitleToAll}
                  onChange={() => setApplyTitleToAll(false)}
                />
                Rename only in this workflow
              </label>
            </div>
            <p className="mt-1 text-xs text-gray-400">
              {applyTitleToAll
                ? "This step's name is shared everywhere it's used — renaming it here updates every workflow that includes this step."
                : "Only this workflow's copy of the step will show the new name; other workflows using this step keep the shared name."}
            </p>
            <div className="mt-3 flex items-end gap-2">
              <Input
                className="w-40"
                label="Step SLA (days)"
                type="number"
                min={1}
                placeholder="No SLA"
                value={slaDays}
                onChange={(e) => setSlaDays(e.target.value)}
              />
              {String(slaDays).trim() !== '' && (
                <button
                  type="button"
                  onClick={() => setSlaDays('')}
                  className="mb-2 text-xs text-gray-400 hover:text-red-600"
                >
                  Remove
                </button>
              )}
            </div>
            <p className="mt-1 text-xs text-gray-400">
              Flag the candidate as overdue if they stay on this step longer than this many days.
            </p>
            <label className="mt-3 flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" className="accent-primary" checked={skipEnabled} onChange={(e) => setSkipEnabled(e.target.checked)} />
              Allow staff to skip this step
            </label>
            <p className="mt-1 text-xs text-gray-400">
              Adds a "Skip Step" action alongside Complete — bypasses required documents and fields. The step's SLA still
              counts against it until it's skipped or completed.
            </p>
          </div>
        )}

        {activeTab === 'checklist' && (
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <p className="block text-sm font-medium text-gray-700">Checklist items</p>
              <button type="button" onClick={addChecklistItem} className="text-xs text-primary hover:underline">
                + Add item
              </button>
            </div>
            <div className="space-y-1.5">
              {checklist.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    className="flex-1"
                    placeholder={`Item ${idx + 1}`}
                    value={item}
                    onChange={(e) => setChecklistItem(idx, e.target.value)}
                  />
                  <button type="button" onClick={() => removeChecklistItem(idx)} className="text-xs text-gray-400 hover:text-red-600">
                    ✕
                  </button>
                </div>
              ))}
              {checklist.length === 0 && <p className="text-xs text-gray-400">No checklist items yet.</p>}
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <p className="block text-sm font-medium text-gray-700">Documents</p>
              <Select
                className="w-52"
                value=""
                onChange={(e) => addDoc(e.target.value)}
                disabled={!availableDocTypes.length}
              >
                <option value="">+ Add document…</option>
                {availableDocTypes.map((t) => (
                  <option key={t.code} value={t.code}>
                    {t.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              {requiredDocs.map((entry) => (
                <div key={entry.code} className="flex items-center justify-between rounded-md border border-gray-200 px-3 py-2">
                  <div>
                    <span className="text-sm text-gray-700">{docTypeName(entry.code)}</span>
                    {entry.depends_on && (
                      <p className="text-xs text-gray-400">
                        Shown when "{fieldLabel(entry.depends_on.field_id)}" = "{entry.depends_on.value}" — set from the Fields tab
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <input
                        type="checkbox"
                        className="accent-primary"
                        checked={entry.required}
                        onChange={(e) => setDocOption(entry.code, { required: e.target.checked })}
                      />
                      Required
                    </label>
                    {entry.required && (
                      <label className="flex items-center gap-1.5 text-xs text-gray-500">
                        <input
                          type="checkbox"
                          className="accent-primary"
                          checked={entry.verify}
                          onChange={(e) => setDocOption(entry.code, { verify: e.target.checked })}
                        />
                        Must be verified
                      </label>
                    )}
                    <button type="button" onClick={() => removeDoc(entry.code)} className="text-xs text-gray-400 hover:text-red-600">
                      ✕
                    </button>
                  </div>
                </div>
              ))}
              {requiredDocs.length === 0 && <p className="text-xs text-gray-400">No documents yet.</p>}
            </div>
          </div>
        )}

        {activeTab === 'fields' && (
          <FieldSectionsEditor
            fields={fields}
            sections={fieldSections}
            onFieldsChange={setFields}
            onSectionsChange={setFieldSections}
            documents={requiredDocs}
            onDocumentsChange={setRequiredDocs}
            docTypeOptions={activeDocTypes}
          />
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Modal>
  );
}
